import { store } from '@/store'
import { updateLastActivity, setSessionId } from '@/store/slices/authSlice'

class SessionService {
  private sessionCheckInterval: number | null = null
  private readonly SESSION_TIMEOUT = 30 * 60 * 1000 // 30 minutes
  private readonly ACTIVITY_CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes

  constructor() {
    this.initializeSession()
  }

  private initializeSession() {
    // Check for existing session on app load
    const state = store.getState()
    if (state.auth.sessionId) {
      this.startSessionMonitoring()
    }
  }

  public startSession(sessionId: string) {
    store.dispatch(setSessionId(sessionId))
    this.startSessionMonitoring()
    this.trackActivity()
  }

  public endSession() {
    this.stopSessionMonitoring()
    store.dispatch(setSessionId(null))
  }

  public trackActivity() {
    store.dispatch(updateLastActivity())
    this.saveToSessionStorage()
  }

  private startSessionMonitoring() {
    this.stopSessionMonitoring() // Clear any existing interval
    
    this.sessionCheckInterval = setInterval(() => {
      this.checkSessionValidity()
    }, this.ACTIVITY_CHECK_INTERVAL)
  }

  private stopSessionMonitoring() {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval)
      this.sessionCheckInterval = null
    }
  }

  private checkSessionValidity() {
    const state = store.getState()
    const { lastActivity, isAuthenticated } = state.auth

    if (!isAuthenticated || !lastActivity) {
      return
    }

    const now = Date.now()
    const timeSinceLastActivity = now - lastActivity

    if (timeSinceLastActivity > this.SESSION_TIMEOUT) {
      console.log('Session expired due to inactivity')
      this.handleSessionExpiry()
    }
  }

  private handleSessionExpiry() {
    // Get user role before clearing state
    const state = store.getState()
    const userRole = state.auth?.user?.role
    
    this.endSession()
    
    // Dispatch logout action
    store.dispatch({ type: 'auth/manualLogout' })
    
    // Show notification
    store.dispatch({
      type: 'ui/showToast',
      payload: {
        type: 'warning',
        message: 'Your session has expired due to inactivity. Please log in again.',
        title: 'Session Expired'
      }
    })

    // Redirect based on user role
    let redirectPath = '/user-login' // Default for staff, department_head, front_worker, etc.
    if (userRole === 'universal_admin' || userRole === 'office_admin') {
      redirectPath = '/nfcrwacontrol'
    } else if (userRole === 'member') {
      redirectPath = '/login'
    }
    
    window.location.href = redirectPath
  }

  private saveToSessionStorage() {
    const state = store.getState()
    const sessionData = {
      sessionId: state.auth.sessionId,
      lastActivity: state.auth.lastActivity,
      userId: state.auth.user?.id,
      timestamp: Date.now()
    }

    try {
      sessionStorage.setItem('nfcrwa_session', JSON.stringify(sessionData))
    } catch (error) {
      console.error('Failed to save session to storage:', error)
    }
  }

  public getSessionFromStorage() {
    try {
      const sessionData = sessionStorage.getItem('nfcrwa_session')
      if (sessionData) {
        return JSON.parse(sessionData)
      }
    } catch (error) {
      console.error('Failed to get session from storage:', error)
    }
    return null
  }

  public clearSessionStorage() {
    try {
      sessionStorage.removeItem('nfcrwa_session')
    } catch (error) {
      console.error('Failed to clear session storage:', error)
    }
  }

  public isSessionValid(): boolean {
    const state = store.getState()
    const { lastActivity, isAuthenticated } = state.auth

    if (!isAuthenticated || !lastActivity) {
      return false
    }

    const now = Date.now()
    const timeSinceLastActivity = now - lastActivity

    return timeSinceLastActivity < this.SESSION_TIMEOUT
  }

  public getSessionInfo() {
    const state = store.getState()
    return {
      sessionId: state.auth.sessionId,
      lastActivity: state.auth.lastActivity,
      isAuthenticated: state.auth.isAuthenticated,
      isValid: this.isSessionValid(),
      timeRemaining: this.getTimeRemaining()
    }
  }

  private getTimeRemaining(): number {
    const state = store.getState()
    const { lastActivity } = state.auth

    if (!lastActivity) {
      return 0
    }

    const now = Date.now()
    const timeSinceLastActivity = now - lastActivity
    const timeRemaining = this.SESSION_TIMEOUT - timeSinceLastActivity

    return Math.max(0, timeRemaining)
  }
}

// Create singleton instance
export const sessionService = new SessionService()

// Auto-track activity on user interactions
if (typeof window !== 'undefined') {
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
  
  const trackActivity = () => {
    sessionService.trackActivity()
  }

  events.forEach(event => {
    document.addEventListener(event, trackActivity, true)
  })
}
